import React, { useEffect, useState } from "react";
import "./PrakritiReportPrintModal.css";
import { prakritiQuestionsData } from "../../../utils/prakritiQuestionsData";
import { getPatientById } from "../../../api/patient";
import { FaPrint, FaTimes, FaLeaf } from "react-icons/fa";

const Field = ({ label, value }) => (
  <div className="pk-field">
    <span className="pk-field-label">{label}</span>
    <span className="pk-field-value">{value || "—"}</span>
  </div>
);

const pickPatient = (...sources) => {
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    // Prefer objects that actually have identity fields
    if (src.name || src.contact || src.phone || src.age || src.gender || src.address) {
      return src;
    }
  }
  return sources.find((s) => s && typeof s === "object") || null;
};

const resolvePatientId = (assessment, patient) =>
  patient?._id ||
  patient?.id ||
  assessment?.patientId ||
  assessment?.patient?._id ||
  assessment?.patient?.id ||
  null;

const PrakritiReportPrintModal = ({ assessment, patient: patientProp, onClose }) => {
  const [fetchedPatient, setFetchedPatient] = useState(null);
  const [fetchingPatient, setFetchingPatient] = useState(false);

  useEffect(() => {
    if (!assessment) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [assessment, onClose]);

  // Always ensure we have full patient demographics for the report
  useEffect(() => {
    if (!assessment) return undefined;

    const hasBasics =
      patientProp?.name &&
      (patientProp?.contact || patientProp?.phone) &&
      patientProp?.age != null;

    if (hasBasics) {
      setFetchedPatient(null);
      return undefined;
    }

    const id = resolvePatientId(assessment, patientProp);
    if (!id) return undefined;

    let cancelled = false;
    const load = async () => {
      try {
        setFetchingPatient(true);
        const res = await getPatientById(id);
        const data =
          res?.data?.result ||
          res?.data?.patient ||
          res?.data?.data ||
          res?.data;
        if (!cancelled && data) {
          setFetchedPatient(data);
        }
      } catch (err) {
        console.error("Failed to load patient for Prakriti report:", err);
      } finally {
        if (!cancelled) setFetchingPatient(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [assessment, patientProp]);

  if (!assessment) return null;

  const handlePrint = () => {
    window.print();
  };

  const getAnswerForQuestion = (qId) => {
    const found = assessment.answers?.find((a) => a.questionNumber === qId);
    return found ? found.selectedOption : "";
  };

  const patient = pickPatient(fetchedPatient, patientProp, assessment.patient) || {};
  const scores = assessment.scores || { vataCount: 0, pittaCount: 0, kaphaCount: 0 };

  const patientName = patient.name || patient.fullName || "—";
  const patientAge = patient.age ?? patient.Age ?? null;
  const patientGender = patient.gender || patient.Gender || "";
  const patientContact = patient.contact || patient.phone || patient.mobile || "";
  const patientAddress = patient.address || patient.Address || "";
  const displayPatientId =
    patient.patientId ||
    patient.customPatientId ||
    patient.patientCode ||
    patient._id ||
    assessment.patientId ||
    "—";

  const reportDate = new Date(assessment.createdAt || Date.now()).toLocaleDateString(
    "en-GB",
    { day: "2-digit", month: "short", year: "numeric" }
  );

  const doshaMeta = {
    A: { name: "वात", en: "Vata", className: "vata" },
    B: { name: "पित्त", en: "Pitta", className: "pitta" },
    C: { name: "कफ", en: "Kapha", className: "kapha" },
  };

  return (
    <div
      className="prakriti-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="prakriti-modal-container" role="dialog" aria-modal="true">
        <div className="prakriti-modal-actions no-print">
          <div className="pk-modal-title-block">
            <span className="pk-modal-icon" aria-hidden="true">
              <FaLeaf />
            </span>
            <div>
              <h3>आयुर्वेदिक प्रकृती परीक्षण अहवाल</h3>
              <p>Prakriti Assessment Report</p>
            </div>
          </div>
          <div className="pk-modal-btns">
            <button type="button" className="btn-print" onClick={handlePrint}>
              <FaPrint /> Print / PDF
            </button>
            <button type="button" className="pk-btn-close" onClick={onClose} aria-label="Close">
              <FaTimes /> Close
            </button>
          </div>
        </div>

        <div className="prakriti-paper printable-content">
          <header className="prakriti-header">
            <div className="pk-hospital-block">
              <p className="pk-hospital-name">अष्टांग आयुर्वेद</p>
              <p className="pk-hospital-sub">सुपर स्पेशालिटी हॉस्पिटल</p>
              <p className="pk-hospital-en">Ashtang Ayurved Superspeciality Hospital</p>
            </div>
            <div className="prakriti-title-badge">आयुर्वेदिक प्रकृती परीक्षण पत्रक</div>
            <div className="pk-meta-row">
              <span>
                <strong>Patient:</strong>{" "}
                {fetchingPatient && !patient.name ? "Loading..." : patientName}
              </span>
              <span>
                <strong>ID:</strong> {displayPatientId}
              </span>
              <span>
                <strong>Date:</strong> {reportDate}
              </span>
            </div>
          </header>

          <section className="prakriti-section">
            <div className="section-title">
              <span className="section-num">01</span>
              <div>
                <strong>वैयक्तिक तपशील</strong>
                <em>Personal Details</em>
              </div>
            </div>
            <div className="grid-2col">
              <Field label="पूर्ण नाव" value={patientName !== "—" ? patientName : null} />
              <Field
                label="वय"
                value={patientAge != null && patientAge !== "" ? `${patientAge} वर्ष` : null}
              />
              <Field label="लिंग" value={patientGender} />
              <Field label="मोबाईल" value={patientContact} />
              <Field
                label="वैवाहिक स्थिती"
                value={assessment.maritalStatus || patient.maritalStatus}
              />
              <Field label="शिक्षण" value={assessment.education || patient.education} />
              <Field label="व्यवसाय" value={assessment.occupation || patient.occupation} />
              <Field label="पत्ता" value={patientAddress} />
            </div>
          </section>

          <section className="prakriti-section">
            <div className="section-title">
              <span className="section-num">02</span>
              <div>
                <strong>शारीरिक मापे आणि जीवनशैली</strong>
                <em>Measurements & Lifestyle</em>
              </div>
            </div>
            <div className="grid-2col">
              <Field label="उंची (सेमी)" value={assessment.physicalMeasurements?.heightCm} />
              <Field label="वजन (किलो)" value={assessment.physicalMeasurements?.weightKg} />
              <Field
                label="व्यायामाचा प्रकार"
                value={assessment.physicalMeasurements?.exerciseType}
              />
              <Field
                label="झोप (तास/रात्र)"
                value={assessment.physicalMeasurements?.sleepHoursPerNight}
              />
            </div>
          </section>

          <section className="prakriti-section">
            <div className="section-title">
              <span className="section-num">03</span>
              <div>
                <strong>आहारविषयक सवयी</strong>
                <em>Dietary Habits</em>
              </div>
            </div>
            <div className="grid-2col">
              <Field label="आहार प्रकार" value={assessment.dietaryHabits?.dietType} />
              <Field label="जेवणाची संख्या" value={assessment.dietaryHabits?.mealsPerDay} />
              <Field label="जेवणाची वेळ" value={assessment.dietaryHabits?.mealTimings} />
              <Field
                label="पाणी पिणे (लिटर)"
                value={assessment.dietaryHabits?.waterIntakeLiters}
              />
              <Field label="पेयांचे सेवन" value={assessment.dietaryHabits?.beverageIntake} />
              <Field label="फळांचे सेवन" value={assessment.dietaryHabits?.fruitConsumption} />
              <Field
                label="भाजीचे सेवन"
                value={assessment.dietaryHabits?.vegetableConsumption}
              />
              <Field
                label="गोड पदार्थांचे सेवन"
                value={assessment.dietaryHabits?.sweetsConsumption}
              />
              <Field
                label="तळलेले / प्रक्रिया केलेले"
                value={assessment.dietaryHabits?.friedOrProcessedFood}
              />
              <Field
                label="उपवास / IF तपशील"
                value={assessment.dietaryHabits?.fastingDetails}
              />
            </div>
          </section>

          <section className="prakriti-section">
            <div className="section-title">
              <span className="section-num">04</span>
              <div>
                <strong>आरोग्य माहिती</strong>
                <em>Health History</em>
              </div>
            </div>
            <div className="grid-1col">
              <Field
                label="ज्ञात वैद्यकीय आजार"
                value={assessment.healthInfo?.knownMedicalIllnesses}
              />
              <Field
                label="पचन / पोटाचे त्रास"
                value={assessment.healthInfo?.digestiveIssues}
              />
              <Field
                label="झालेली शस्त्रक्रिया"
                value={assessment.healthInfo?.pastSurgeries}
              />
              <Field label="अन्नाची ऍलर्जी" value={assessment.healthInfo?.foodAllergies} />
              <Field
                label="सध्याची औषधे / Supplements"
                value={assessment.healthInfo?.currentMedications}
              />
              <Field
                label="इतर सवयी / व्यसने"
                value={assessment.healthInfo?.habitsAndAddictions}
              />
            </div>
          </section>

          <div className="page-break" />

          <section className="prakriti-section">
            <div className="section-title">
              <span className="section-num">05</span>
              <div>
                <strong>प्रकृती परीक्षण प्रश्नावली</strong>
                <em>{prakritiQuestionsData.length}-Question Assessment</em>
              </div>
            </div>

            <div className="pk-legend no-print">
              <span className="pk-legend-item vata">A · वात</span>
              <span className="pk-legend-item pitta">B · पित्त</span>
              <span className="pk-legend-item kapha">C · कफ</span>
            </div>

            <div className="questions-grid">
              {prakritiQuestionsData.map((q) => {
                const selectedOpt = getAnswerForQuestion(q.id);
                const selectedMeta = doshaMeta[selectedOpt];
                return (
                  <div key={q.id} className="q-print-card">
                    <div className="q-print-header">
                      <div className="q-print-title">
                        <span className="q-print-num">{String(q.id).padStart(2, "0")}</span>
                        <div>
                          <span className="q-mr">{q.titleMr}</span>
                          <span className="q-en">{q.titleEn}</span>
                        </div>
                      </div>
                      <span
                        className={`ans-badge ${selectedMeta ? selectedMeta.className : ""}`}
                      >
                        {selectedOpt
                          ? `उत्तर: ${selectedOpt} · ${selectedMeta.name}`
                          : "उत्तर: —"}
                      </span>
                    </div>
                    <div className="q-print-options">
                      {["A", "B", "C"].map((optKey) => {
                        const opt = q.options[optKey];
                        const isSelected = selectedOpt === optKey;
                        const meta = doshaMeta[optKey];
                        return (
                          <div
                            key={optKey}
                            className={`q-opt-item ${meta.className} ${
                              isSelected ? "selected" : ""
                            }`}
                          >
                            <span className="q-opt-key">{optKey}</span>
                            <span className="q-opt-text">{opt.mr}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="prakriti-section scoring-section">
            <div className="section-title">
              <span className="section-num">06</span>
              <div>
                <strong>प्रकृती मोजणी व निष्कर्ष</strong>
                <em>Scoring Summary & Diagnosis</em>
              </div>
            </div>

            <div className="score-summary-row">
              <div className="score-box vata">
                <span className="score-box-label">वात (A)</span>
                <strong className="score-box-num">{scores.vataCount}</strong>
                <span className="score-box-en">Vata</span>
              </div>
              <div className="score-box pitta">
                <span className="score-box-label">पित्त (B)</span>
                <strong className="score-box-num">{scores.pittaCount}</strong>
                <span className="score-box-en">Pitta</span>
              </div>
              <div className="score-box kapha">
                <span className="score-box-label">कफ (C)</span>
                <strong className="score-box-num">{scores.kaphaCount}</strong>
                <span className="score-box-en">Kapha</span>
              </div>
            </div>

            <div className="diagnosis-conclusion">
              <div className="diag-row">
                <span className="diag-label">प्रकृती निष्कर्ष</span>
                <span className="diag-value highlight">
                  {assessment.dominantPrakriti || "—"}
                </span>
              </div>
              {assessment.diagnosisConclusion && (
                <div className="diag-row">
                  <span className="diag-label">वैद्यकीय टिप्पणी / निदान</span>
                  <span className="diag-value">{assessment.diagnosisConclusion}</span>
                </div>
              )}
              {assessment.notes && (
                <div className="diag-row">
                  <span className="diag-label">इतर टीप</span>
                  <span className="diag-value">{assessment.notes}</span>
                </div>
              )}
            </div>
          </section>

          <footer className="prakriti-footer-signature">
            <div className="sig-block">
              <span className="sig-label">दिनांक</span>
              <span className="sig-line">{reportDate}</span>
            </div>
            <div className="sig-block right">
              <span className="sig-label">वैद्य / डॉक्टरांची सही</span>
              <span className="sig-line blank" />
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default PrakritiReportPrintModal;
